import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { FieldGroup, Spinner } from '@xsolla/ui-core';

import { getMfaSettings, setMfaSettings } from '../../../api';
import { MfaValue } from '../../../api/configurations/mfa';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { HorizontalCenterBlock, ShortSettingsBlock } from '../../../styled';
import { FieldInput, FieldSwitch, FormFooter, HeaderTitle, Notice } from '../../ui';
import { RiskWrapper, UsageSubtitle, UsageTitle, UsageWrapper } from './style';
import { mfaUrlValidation } from './validator';

const defaultState: MfaValue = {
  mfa_types: [
    {
      mfa_type: '',
      priority: 0
    }
  ],
  mfa_url: ''
};

type MfaForm = MfaValue & {
  mfa_toggle: boolean;
};

const MfaSettings = ({ history, match }: RouteComponentProps): JSX.Element => {
  const { navigationPath } = usePaths();
  const { projectId } = match.params as RouteParams;
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();
  const { currentProject } = useCurrentProject();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const [fetchingData, setFetchingData] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [mfaData, setMfaData] = useState<MfaValue>(defaultState);

  const backToNavigation = () => history.push({ pathname: navigationPath(projectId) });

  useEffect(() => {
    if (currentProject?.id) {
      getMfaSettings(currentProject.id)
        .then((res) => {
          setMfaData(res);
        })
        .catch((err) => {
          showErrorNotify(err);
        })
        .finally(() => setFetchingData(false));
    }
  }, [currentProject?.id]);

  const defaultMfaUrl = 'https://login-widget.xsolla.com/latest/otp';
  const onSubmit = async (values: MfaForm) => {
    try {
      if (currentProject?.id) {
        let payload;

        setFetching(false);

        if (values.mfa_toggle) {
          payload = {
            mfa_types: [
              {
                mfa_type: 'email',
                priority: 1
              }
            ],
            mfa_url: values.mfa_url || defaultMfaUrl
          };
        } else {
          payload = {
            mfa_types: []
          };
        }
        await setMfaSettings(currentProject?.id, payload);

        showSuccessNotify();
        setFetching(false);
      }
    } catch (e) {
      showErrorNotify(e);
      setFetching(false);
    }
  };

  // ToDo: add extraValue (enabled/disabled) after integrating with API
  const sendClickEvent = (name: string) => {
    xsollaAnalytics.sendClick('login_pa_module__mfa__method_click', name);
  };

  if (fetchingData) {
    return (
      <HorizontalCenterBlock>
        <Spinner />
      </HorizontalCenterBlock>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      validate={mfaUrlValidation}
      initialValues={{
        mfa_url: mfaData.mfa_url,
        mfa_type: 'email',
        priority: 1,
        mfa_toggle: mfaData.mfa_types.length > 0
      }}>
      {({ handleSubmit, pristine }) => {
        return (
          <>
            <HorizontalCenterBlock>
              <ShortSettingsBlock>
                <HeaderTitle
                  title={intl.formatMessage({ id: 'mfa.settings.title' })}
                  subtitle={intl.formatMessage({ id: 'mfa.settings.subtitle' })}
                  notification={intl.formatMessage({ id: 'app.contact-integrator.notification' })}
                  notificationIcon={Notice}
                />

                <UsageWrapper>
                  <UsageTitle>{intl.formatMessage({ id: 'mfa.settings.usage.title' })}</UsageTitle>
                  <UsageSubtitle>
                    {intl.formatMessage({ id: 'mfa.settings.usage.subtitle' })}
                  </UsageSubtitle>
                </UsageWrapper>

                <RiskWrapper>
                  <FieldSwitch
                    name="mfa_toggle"
                    label={intl.formatMessage({ id: 'mfa.settings.risk.title' })}
                    caption={intl.formatMessage({ id: 'mfa.settings.risk.description' })}
                    onClick={() => sendClickEvent('risk')}
                  />
                </RiskWrapper>

                <FieldGroup label={intl.formatMessage({ id: 'mfa.settings.url.title' })} size="md">
                  <FieldInput
                    name="mfa_url"
                    subLabel={intl.formatMessage({
                      id: 'mfa.settings.url.description'
                    })}
                    placeholder={defaultMfaUrl}
                    // errorMessage={getError('from_email_name')}
                  />
                </FieldGroup>
              </ShortSettingsBlock>
            </HorizontalCenterBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              disabled={pristine}
              fetching={fetching}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(MfaSettings);
